import { useState } from "react";

import { useQuery, type UseQueryResult } from "@tanstack/react-query";

import type {
  Imaging,
  ImagingTrackingNotification,
  SharedStats,
} from "@/features/imagings/schemas/imagings";
import { TrackingService } from "@/features/imagings/services/trackingService";

import type { DocumentSnapshot } from "firebase/firestore";

const ITEMS_PER_PAGE = 5;

interface UseSharedRecordsOptions {
  imaging?: Imaging;
  objectType?: "imaging_access" | "report_access";
}

/**
 * Hook para obtener registros de compartidos con paginación
 */
export function useSharedRecords({
  imaging,
}: UseSharedRecordsOptions = {}): UseQueryResult<{
  trackingNotifications: ImagingTrackingNotification[];
  hasNextPage: boolean;
  totalCount: number;
  lastDoc: DocumentSnapshot | null;
}> & {
  nextPage: () => void;
  prevPage: () => void;
  hasNextPage: boolean;
  hasPrevPage: boolean;
  currentPage: number;
} {
  const [currentPage, setCurrentPage] = useState(0);
  const [lastDoc, setLastDoc] = useState<DocumentSnapshot | undefined>(undefined);
  const [pageHistory, setPageHistory] = useState<DocumentSnapshot[]>([]);

  const query = useQuery({
    queryKey: ["tracking_notifications", imaging?.uid, imaging?.iuid, currentPage],
    queryFn: async () => {
      if (!imaging?.uid) {
        throw new Error("Se requiere imagingId");
      }

      const trackingNotificationsResult = await TrackingService.getTrackingNotifications(
        imaging.uid,
        lastDoc,
        ITEMS_PER_PAGE
      );

      return {
        trackingNotifications: trackingNotificationsResult.notifications,
        hasNextPage: trackingNotificationsResult.notifications.length === ITEMS_PER_PAGE,
        totalCount: await TrackingService.getTrackingNotificationsCount(imaging.uid),
        lastDoc: trackingNotificationsResult.lastDoc,
      };
    },
    enabled: !!imaging?.uid,
  });

  const nextPage = () => {
    if (query.data?.lastDoc && lastDoc) {
      setPageHistory((prev) => [...prev, lastDoc]);
      setLastDoc(query.data.lastDoc);
      setCurrentPage((prev) => prev + 1);
    }
  };

  const prevPage = () => {
    if (currentPage > 0) {
      const newHistory = [...pageHistory];
      const previousDoc = newHistory.pop();
      setPageHistory(newHistory);
      setLastDoc(previousDoc);
      setCurrentPage((prev) => prev - 1);
    }
  };

  return {
    ...query,
    nextPage,
    prevPage,
    hasNextPage: query.data?.hasNextPage || false,
    hasPrevPage: currentPage > 0,
    currentPage,
  };
}

/**
 * Hook para obtener estadísticas de compartidos
 */
export function useSharedStats(imagingId?: string): UseQueryResult<SharedStats> {
  return useQuery({
    queryKey: ["tracking_stats", imagingId],
    queryFn: async () => {
      if (!imagingId) {
        throw new Error("Se requiere imagingId");
      }

      return TrackingService.getTrackingStats(imagingId);
    },
    enabled: !!imagingId,
  });
}
